import PropTypes from 'prop-types'
import { useState, useCallback, useEffect } from 'react'
import { Modal } from '@calendly/ui/components/modal'
import { Button } from '@calendly/ui/components/button'
import { TitleText } from '@calendly/ui/components/typography'
import { VerificationCodeField } from '@calendly/ui/form/verificaton-code-field'

import { useI18n } from 'locales'

import { PhoneVerificationSuccessBanner } from './success_banner'
import { ResendPrompt } from './resend_prompt'
import { SkipSMSConfirmation, VerifySMSSuccessConfirmation } from './confirmation_step'
import {
  ModalCard,
  SuccessBannerSection,
  Divider,
  ModalBody,
  Footer,
} from './styles'

const CODE_LENGTH = 6
const STEP_VERIFY = 'verify'
const STEP_SKIP_CONFIRM = 'skip_confirm'
const STEP_VERIFY_SUCCESS = 'verify_success'

export const PhoneVerificationModal = ({
  isOpen,
  phoneNumber,
  onVerify,
  onResend,
  onSkip,
  onComplete,
  isVerifying = false,
  isResending = false,
  error,
}) => {
  const { i18n } = useI18n()
  const [step, setStep] = useState(STEP_VERIFY)
  const [verificationCode, setVerificationCode] = useState('')
  const canVerify = verificationCode.length === CODE_LENGTH

  useEffect(() => {
    if (!isOpen) {
      setStep(STEP_VERIFY)
      setVerificationCode('')
    }
  }, [isOpen])

  const handleComplete = useCallback(() => {
    onComplete?.()
  }, [onComplete])

  const handleConfirmSkip = useCallback(() => {
    onSkip?.()
    onComplete?.()
  }, [onComplete, onSkip])

  const handleSkip = useCallback(() => {
    setStep(STEP_SKIP_CONFIRM)
  }, [])

  const handleModalClose = useCallback(() => {
    if (step === STEP_SKIP_CONFIRM) {
      handleConfirmSkip()
    } else if (step === STEP_VERIFY_SUCCESS) {
      handleComplete()
    } else {
      handleSkip()
    }
  }, [step, handleConfirmSkip, handleComplete, handleSkip])

  const handleVerify = useCallback(async () => {
    if (!canVerify || isVerifying) return
    try {
      await onVerify?.(verificationCode)
      setStep(STEP_VERIFY_SUCCESS)
    } catch {
      // Stay on the verify step; the parent surfaces the failure via `error`.
    }
  }, [canVerify, isVerifying, onVerify, verificationCode])

  const handleResend = useCallback(async () => {
    setVerificationCode('')
    await onResend?.()
  }, [onResend])

  const handleCodeChange = useCallback((code) => {
    setVerificationCode(code)
  }, [])

  if (!isOpen) return null

  const renderStepContent = () => {
    if (step === STEP_SKIP_CONFIRM) {
      return <SkipSMSConfirmation onConfirm={handleConfirmSkip} />
    }
    if (step === STEP_VERIFY_SUCCESS) {
      return (
        <VerifySMSSuccessConfirmation
          phoneNumber={phoneNumber}
          onConfirm={handleComplete}
        />
      )
    }
    return (
      <ModalBody>
        <TitleText level={2} size="large">
          {i18n.t('booking.phone_verification.title')}
        </TitleText>
        <ResendPrompt
          phoneNumber={phoneNumber}
          onResend={handleResend}
          isResending={isResending}
        />
        <VerificationCodeField
          error={error}
          onChange={handleCodeChange}
          autoFocus
        />
        <Footer>
          <Button
            decoration="secondary-outline"
            type="button"
            onClick={handleSkip}
            disabled={isVerifying}
          >
            {i18n.t('booking.phone_verification.skip')}
          </Button>
          <Button
            type="button"
            onClick={handleVerify}
            loading={isVerifying}
            disabled={!canVerify || isVerifying}
          >
            {i18n.t('booking.phone_verification.verify')}
          </Button>
        </Footer>
      </ModalBody>
    )
  }

  return (
    <Modal isOpen={isOpen} isBlocking onClose={handleModalClose}>
      <ModalCard>
        <SuccessBannerSection>
          <PhoneVerificationSuccessBanner />
        </SuccessBannerSection>
        <Divider role="separator" />
        {renderStepContent()}
      </ModalCard>
    </Modal>
  )
}

PhoneVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  phoneNumber: PropTypes.string,
  onVerify: PropTypes.func,
  onResend: PropTypes.func,
  onSkip: PropTypes.func,
  onComplete: PropTypes.func,
  isVerifying: PropTypes.bool,
  isResending: PropTypes.bool,
  error: PropTypes.string,
}
